import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import {
  newUserObjectivesQuery,
  updateUserObjectivesQuery,
  deleteUserObjectivesQuery,
  getTeamMembers,
  getObjectivesQuery,
  getObjectivesRealizedQuery,
} from "./queries";
import { QUERY_STATUS, SCOPES } from "constants/constants";
import { useUserContext } from "api/user/UserProvider";
import { canUserPerformAction } from "access-roles/general-functions";
import { ROLE_GET_OBJECTIVE, ROLE_READ_OBJECTIVE } from "access-roles/roles";
import { streamingDownload } from "utils/streamingDownload";

const ObjectivesContext = createContext({});

export const useObjectivesContext = () => useContext(ObjectivesContext);

export const ObjectivesProvider = ({ ...props }) => {
  const [objectives, setObjectives] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [objectivesRealized, setObjectivesRealized] = useState([]);
  const { userScope, userRoleList } = useUserContext();
  const [objStatus, setObjStatus] = useState(QUERY_STATUS.INITIAL);
  const [
    loadObjectivesAndRealizedExportStatus,
    setLoadObjectivesAndRealizedExportStatus,
  ] = useState(QUERY_STATUS.INITIAL);

  const createNewUserObjectives = useCallback(async (data) => {
    try {
      const result = await newUserObjectivesQuery(data);
      setObjectives(result || []);
    } catch (e) {
      console.error("newUserObjectives", e);
      throw e;
    }
  }, []);

  const updateUserObjectives = useCallback(async (data) => {
    try {
      setObjectives(await updateUserObjectivesQuery(data));
    } catch (e) {
      console.error("updateUserObjectives", e);
      throw e;
    }
  }, []);

  const deleteUserObjectives = useCallback(async (data) => {
    try {
      await deleteUserObjectivesQuery(data);
    } catch (e) {
      console.error("deleteUserObjectives", e);
      throw e;
    }
  }, []);

  const fetchTeamMembers = useCallback(async () => {
    try {
      const members = await getTeamMembers();
      setTeamMembers(members || []);
    } catch (e) {
      console.error("fetchTeamMembers", e);
      throw e;
    }
  }, []);

  const getObjectives = useCallback(
    async ({ userScope }) => {
      try {
        if (userScope !== SCOPES.MACRO) return;
        setObjStatus(QUERY_STATUS.LOADING);
        const result = await getObjectivesQuery({ userScope });
        setObjectives(result || []);
        setObjStatus(QUERY_STATUS.SUCCESS);
      } catch (e) {
        setObjStatus(QUERY_STATUS.ERROR);
        console.error("getTeamObjectives", e);
        throw e;
      }
    },
    [userScope]
  );

  const getObjectivesRealized = useCallback(
    async ({ userScope, year, branch_ids, manager_ids }) => {
      const requiredRole =
        userScope === SCOPES.READ ? ROLE_READ_OBJECTIVE : ROLE_GET_OBJECTIVE;
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: requiredRole,
        })
      ) {
        return;
      }
      try {
        const objectivesRealizedData = await getObjectivesRealizedQuery({
          userScope,
          year,
          branch_ids,
          manager_ids,
        });
        setObjectivesRealized(objectivesRealizedData || []);
      } catch (e) {
        console.error("fetchTeamObjectivesRealized", e);
        throw e;
      }
    },
    [userScope, userRoleList]
  );

  const getObjectivesAndRealizedExportFile = useCallback(
    async ({ lang, queryParams }) => {
      try {
        setLoadObjectivesAndRealizedExportStatus(QUERY_STATUS.LOADING);
        await streamingDownload({
          path: `/datatrack/objectives/realized/export?scope=${userScope}&year=${queryParams.year}&lang=${lang}`,
          fileName: `objectives_and_realized_${queryParams.year}_export_${lang}.xlsx`,
          mimeType:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        setLoadObjectivesAndRealizedExportStatus(QUERY_STATUS.SUCCESS);
      } catch (error) {
        setLoadObjectivesAndRealizedExportStatus(QUERY_STATUS.ERROR);
        console.error(
          "Erreur lors de la génération ou du téléchargement du fichier:",
          error
        );
      }
    },
    [userScope]
  );

  const values = useMemo(
    () => ({
      objStatus,
      objectives,
      setObjectives,
      createNewUserObjectives,
      getObjectives,
      updateUserObjectives,
      deleteUserObjectives,
      teamMembers,
      fetchTeamMembers,
      objectivesRealized,
      getObjectivesRealized,
      userScope,
      getObjectivesAndRealizedExportFile,
      loadObjectivesAndRealizedExportStatus,
      setLoadObjectivesAndRealizedExportStatus,
    }),
    [
      objStatus,
      userScope,
      objectives,
      teamMembers,
      objectivesRealized,
      getObjectivesAndRealizedExportFile,
      loadObjectivesAndRealizedExportStatus,
    ]
  );

  return <ObjectivesContext.Provider value={values} {...props} />;
};
